import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase, Article } from "../../lib/supabase";
import { Button } from "../../components/ui/button";
import {
  SidebarToggle,
  useAdminLayout,
} from "../../components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  ArrowLeft,
  CheckCircle,
  ImageIcon,
  FileText,
  Info,
  User,
  Calendar,
  Clock,
  Tag,
} from "lucide-react";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useToast } from "../../hooks/use-toast";
import HtmlEditor from "../../components/admin/HtmlEditor";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";

// Định nghĩa kiểu dữ liệu cho errors và category
type FormErrorType = {
  title?: string;
  slug?: string;
  category_id?: string;
  content?: string;
  excerpt?: string;
  author?: string;
  image_url?: string;
  [key: string]: string | undefined;
};

type CategoryType = {
  id: string;
  name: string;
  slug?: string;
  [key: string]: string | undefined;
};

type SubcategoryType = {
  id: string;
  name: string;
  slug?: string;
  category_id: string;
  [key: string]: string | undefined;
};

const ArticleEditor = () => {
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const [activeTab, setActiveTab] = useState("basic");

  const [article, setArticle] = useState<Partial<Article>>({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category_id: "",
    author: "",
    date: new Date().toISOString().split("T")[0],
    read_time: "5 phút",
    image_url: "",
  });

  const [categories, setCategories] = useState<CategoryType[]>([]);
  const [subcategories, setSubcategories] = useState<SubcategoryType[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingArticle, setSavingArticle] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrorType>({});
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const navigate = useNavigate();
  const { toast } = useToast();
  const { isMobile } = useAdminLayout();

  // Chuyển tab khi có lỗi
  useEffect(() => {
    if (
      formErrors.title ||
      formErrors.slug ||
      formErrors.category_id ||
      formErrors.excerpt
    ) {
      setActiveTab("basic");
    } else if (formErrors.content) {
      setActiveTab("content");
    } else if (formErrors.image_url) {
      setActiveTab("image");
    } else if (formErrors.author) {
      setActiveTab("more");
    }
  }, [formErrors]);

  useEffect(() => {
    loadCategories();
    if (isEditMode) {
      loadArticle();
    }
  }, [id]);

  useEffect(() => {
    return () => {
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
    };
  }, [imagePreview]);

  const loadArticle = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      setArticle(data);
      if (data.category_id) {
        loadSubcategories(data.category_id);
      }
    } catch (error) {
      console.error("Error loading article:", error);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải thông tin bài viết",
        variant: "destructive",
      });
      navigate("/admin/articles");
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("name");

      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error("Error loading categories:", error);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải danh sách danh mục",
        variant: "destructive",
      });
    }
  };

  const loadSubcategories = async (categoryId: string) => {
    try {
      const { data, error } = await supabase
        .from("subcategories")
        .select("*")
        .eq("category_id", categoryId)
        .order("name");

      if (error) throw error;
      setSubcategories(data || []);
    } catch (error) {
      console.error("Error loading subcategories:", error);
      toast({
        title: "Lỗi tải dữ liệu",
        description: "Không thể tải danh sách danh mục con",
        variant: "destructive",
      });
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    setArticle({
      ...article,
      category_id: categoryId,
      subcategory_id: undefined,
    });
    loadSubcategories(categoryId);
  };

  const validateForm = () => {
    const errors: FormErrorType = {};
    if (!article.title) errors.title = "Tiêu đề không được để trống";
    if (!article.slug) errors.slug = "Slug không được để trống";
    if (!article.category_id)
      errors.category_id = "Danh mục không được để trống";
    if (!article.content) errors.content = "Nội dung không được để trống";
    if (!article.excerpt) errors.excerpt = "Tóm tắt không được để trống";
    if (!article.author) errors.author = "Tác giả không được để trống";
    if (!article.image_url) errors.image_url = "Ảnh không được để trống";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-");
  };

  const handleTitleChange = (title: string) => {
    setArticle({
      ...article,
      title,
      slug: generateSlug(title),
    });
  };

  const handleSaveArticle = async () => {
    if (!validateForm()) return;

    const now = new Date().toISOString();
    setSavingArticle(true);

    try {
      if (!isEditMode) {
        const { data, error } = await supabase
          .from("articles")
          .insert({
            ...article,
            created_at: now,
            updated_at: now,
          })
          .select();

        if (error) throw error;

        toast({
          title: "Thành công",
          description: "Đã thêm bài viết mới",
          variant: "default",
        });
      } else {
        const { data, error } = await supabase
          .from("articles")
          .update({
            ...article,
            updated_at: now,
          })
          .eq("id", id!)
          .select();

        if (error) throw error;

        toast({
          title: "Thành công",
          description: "Đã cập nhật bài viết",
          variant: "default",
        });
      }

      navigate("/admin/articles");
    } catch (error: unknown) {
      console.error("Error saving article:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Lỗi không xác định";
      toast({
        title: "Lỗi",
        description: `Không thể ${
          isEditMode ? "cập nhật" : "thêm"
        } bài viết: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setSavingArticle(false);
    }
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>
  ) => {
    let file: File | null = null;

    // Xử lý khi người dùng chọn file qua input
    if (
      "target" in event &&
      event.target instanceof HTMLInputElement &&
      event.target.files
    ) {
      file = event.target.files[0];
    }
    // Xử lý khi người dùng kéo thả file
    else if ("dataTransfer" in event && event.dataTransfer) {
      file = event.dataTransfer.files[0];
    }

    if (!file) {
      setUploadError("Vui lòng chọn một tệp tin ảnh");
      return;
    }

    // Kiểm tra type của file
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/)) {
      setUploadError("Chỉ chấp nhận file hình ảnh (JPEG, PNG, GIF, WEBP)");
      return;
    }

    try {
      setUploadingImage(true);
      setUploadError(null);

      // Tạo tên file ngẫu nhiên để tránh trùng lặp
      const fileExt = file.name.split(".").pop();
      const fileName = `${Math.random()
        .toString(36)
        .substring(2)}-${Date.now()}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      // Tạo preview URL cho ảnh được chọn
      if (imagePreview) {
        URL.revokeObjectURL(imagePreview);
      }
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);

      // Upload ảnh lên Supabase Storage
      const { data, error } = await supabase.storage
        .from("images")
        .upload(filePath, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (error) {
        throw error;
      }

      // Lấy URL public cho ảnh đã upload
      const { data: publicUrlData } = supabase.storage
        .from("images")
        .getPublicUrl(filePath);

      // Cập nhật state với đường dẫn ảnh
      setArticle({
        ...article,
        image_url: publicUrlData.publicUrl,
      });

      toast({
        title: "Thành công",
        description: "Đã tải ảnh lên",
        variant: "default",
      });
    } catch (error: unknown) {
      console.error("Error uploading image:", error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Lỗi không xác định khi tải ảnh lên";
      setUploadError(errorMessage);
      toast({
        title: "Lỗi",
        description: "Không thể tải ảnh lên",
        variant: "destructive",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    handleImageUpload(event);
  };

  // Phần render chính
  return (
    <div className="w-full px-2 pb-8">
      <div className="flex items-center mb-4">
        {isMobile && <SidebarToggle className="mx-2" />}
        <Button
          variant="ghost"
          onClick={() => navigate("/admin/articles")}
          className="mr-1"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          <span>Quay lại</span>
        </Button>
        <h1 className="text-lg font-semibold truncate">
          {isEditMode ? "Chỉnh sửa" : "Thêm mới"}
        </h1>
      </div>

      {loading ? (
        <div className="text-center py-10">Đang tải...</div>
      ) : (
        <Card className="w-full">
          <CardHeader className="px-4 py-3 border-b">
            <CardTitle className="text-base flex items-center truncate">
              {article.title
                ? article.title
                : isEditMode
                ? "Chỉnh sửa bài viết"
                : "Thêm bài viết mới"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="w-full"
            >
              <TabsList className="w-full grid grid-cols-4 rounded-none border-b">
                <TabsTrigger
                  value="basic"
                  className="flex items-center text-xs py-1"
                >
                  <FileText className="h-3 w-3 mr-1" />
                  <span>Cơ bản</span>
                </TabsTrigger>
                <TabsTrigger
                  value="content"
                  className="flex items-center text-xs py-1"
                >
                  <Info className="h-3 w-3 mr-1" />
                  <span>Nội dung</span>
                </TabsTrigger>
                <TabsTrigger
                  value="image"
                  className="flex items-center text-xs py-1"
                >
                  <ImageIcon className="h-3 w-3 mr-1" />
                  <span>Hình ảnh</span>
                </TabsTrigger>
                <TabsTrigger
                  value="more"
                  className="flex items-center text-xs py-1"
                >
                  <User className="h-3 w-3 mr-1" />
                  <span>Bổ sung</span>
                </TabsTrigger>
              </TabsList>

              {/* Tab Thông tin cơ bản */}
              <TabsContent
                value="basic"
                className="p-4 space-y-4 focus-visible:outline-none"
              >
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="title">
                      Tiêu đề <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="title"
                      value={article.title || ""}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      className={`${
                        formErrors.title ? "border-red-500" : ""
                      } w-full`}
                    />
                    {formErrors.title && (
                      <p className="text-xs text-red-500">{formErrors.title}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="slug">
                      Slug <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="slug"
                      value={article.slug || ""}
                      onChange={(e) =>
                        setArticle({ ...article, slug: e.target.value })
                      }
                      className={`${
                        formErrors.slug ? "border-red-500" : ""
                      } w-full`}
                    />
                    {formErrors.slug && (
                      <p className="text-xs text-red-500">{formErrors.slug}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">
                      Danh mục <span className="text-red-500">*</span>
                    </Label>
                    <Select
                      value={article.category_id}
                      onValueChange={handleCategoryChange}
                    >
                      <SelectTrigger
                        className={`w-full ${
                          formErrors.category_id ? "border-red-500" : ""
                        }`}
                      >
                        <SelectValue placeholder="Chọn danh mục" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.category_id && (
                      <p className="text-xs text-red-500">
                        {formErrors.category_id}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subcategory">Danh mục con</Label>
                    <Select
                      value={article.subcategory_id}
                      onValueChange={(value) =>
                        setArticle({ ...article, subcategory_id: value })
                      }
                      disabled={
                        !article.category_id || subcategories.length === 0
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Chọn danh mục con" />
                      </SelectTrigger>
                      <SelectContent>
                        {subcategories.map((subcategory) => (
                          <SelectItem
                            key={subcategory.id}
                            value={subcategory.id}
                          >
                            {subcategory.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="excerpt">
                      Tóm tắt <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="excerpt"
                      value={article.excerpt || ""}
                      onChange={(e) =>
                        setArticle({ ...article, excerpt: e.target.value })
                      }
                      rows={3}
                      className={`${
                        formErrors.excerpt ? "border-red-500" : ""
                      } w-full`}
                    />
                    {formErrors.excerpt && (
                      <p className="text-xs text-red-500">
                        {formErrors.excerpt}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-between pt-4 mt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => navigate("/admin/articles")}
                    size="sm"
                  >
                    Hủy
                  </Button>
                  <Button onClick={() => setActiveTab("content")} size="sm">
                    Tiếp tục
                  </Button>
                </div>
              </TabsContent>

              {/* Tab Nội dung */}
              <TabsContent
                value="content"
                className="p-4 space-y-4 focus-visible:outline-none flex flex-col"
              >
                <div className="flex-grow overflow-hidden">
                  <HtmlEditor
                    value={article.content || ""}
                    onChange={(value) =>
                      setArticle({ ...article, content: value })
                    }
                    error={formErrors.content}
                    rows={18}
                  />
                </div>

                <div className="flex justify-between pt-4 mt-4 border-t mt-auto">
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab("basic")}
                    size="sm"
                  >
                    Quay lại
                  </Button>
                  <Button onClick={() => setActiveTab("image")} size="sm">
                    Tiếp tục
                  </Button>
                </div>
              </TabsContent>

              {/* Tab Hình ảnh */}
              <TabsContent
                value="image"
                className="p-4 space-y-4 focus-visible:outline-none"
              >
                <div className="space-y-3">
                  <Label htmlFor="image_upload">
                    Ảnh bài viết <span className="text-red-500">*</span>
                  </Label>
                  <div
                    className={`border-2 border-dashed rounded-md p-4 
                      ${
                        formErrors.image_url
                          ? "border-red-500"
                          : "border-gray-300 dark:border-gray-700"
                      }
                      ${uploadingImage ? "bg-gray-100 dark:bg-gray-800" : ""}
                      hover:bg-gray-50 dark:hover:bg-gray-800 transition-all
                      flex flex-col items-center justify-center cursor-pointer w-full`}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={() =>
                      document.getElementById("image_upload")?.click()
                    }
                  >
                    {imagePreview || article.image_url ? (
                      <div className="relative w-full flex flex-col items-center">
                        <img
                          src={imagePreview || article.image_url}
                          alt="Ảnh xem trước"
                          className="max-h-[200px] w-auto object-contain"
                        />
                        <div className="mt-3 text-center w-full">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Kéo thả hoặc nhấp vào để thay đổi ảnh
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        <ImageIcon className="h-10 w-10 text-gray-400 mb-2" />
                        <p className="text-xs font-medium text-center">
                          Kéo thả hoặc nhấp vào để tải ảnh lên
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-center">
                          Hỗ trợ định dạng JPG, PNG, GIF, WEBP
                        </p>
                      </>
                    )}
                    {uploadingImage && (
                      <div className="mt-2 flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary mr-2"></div>
                        <p className="text-xs">Đang tải lên...</p>
                      </div>
                    )}
                    <input
                      type="file"
                      id="image_upload"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={uploadingImage}
                    />
                  </div>
                  {uploadError && (
                    <p className="text-xs text-red-500">{uploadError}</p>
                  )}
                  {formErrors.image_url && (
                    <p className="text-xs text-red-500">
                      {formErrors.image_url}
                    </p>
                  )}
                  {article.image_url && (
                    <p className="text-xs text-gray-500 truncate break-all">
                      {article.image_url}
                    </p>
                  )}
                </div>

                <div className="flex justify-between pt-4 mt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab("content")}
                    size="sm"
                  >
                    Quay lại
                  </Button>
                  <Button onClick={() => setActiveTab("more")} size="sm">
                    Tiếp tục
                  </Button>
                </div>
              </TabsContent>

              {/* Tab Thông tin thêm */}
              <TabsContent
                value="more"
                className="p-4 space-y-4 focus-visible:outline-none"
              >
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="author">
                      Tác giả <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="author"
                      value={article.author || ""}
                      onChange={(e) =>
                        setArticle({ ...article, author: e.target.value })
                      }
                      className={`${
                        formErrors.author ? "border-red-500" : ""
                      } w-full`}
                    />
                    {formErrors.author && (
                      <p className="text-xs text-red-500">
                        {formErrors.author}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="author_title">Chức danh tác giả</Label>
                    <Input
                      id="author_title"
                      value={article.author_title || ""}
                      onChange={(e) =>
                        setArticle({ ...article, author_title: e.target.value })
                      }
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="author_bio">Tiểu sử tác giả</Label>
                    <Textarea
                      id="author_bio"
                      value={article.author_bio || ""}
                      onChange={(e) =>
                        setArticle({ ...article, author_bio: e.target.value })
                      }
                      rows={3}
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="date">Ngày đăng</Label>
                      <Input
                        id="date"
                        type="date"
                        value={article.date || ""}
                        onChange={(e) =>
                          setArticle({ ...article, date: e.target.value })
                        }
                        className="w-full"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="read_time">Thời gian đọc</Label>
                      <Input
                        id="read_time"
                        value={article.read_time || ""}
                        onChange={(e) =>
                          setArticle({ ...article, read_time: e.target.value })
                        }
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-2 pt-4 mt-4 border-t">
                  <Button
                    onClick={handleSaveArticle}
                    disabled={savingArticle}
                    className="flex items-center w-full"
                  >
                    {savingArticle ? (
                      <>Đang lưu...</>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        {isEditMode ? "Cập nhật bài viết" : "Tạo bài viết mới"}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setActiveTab("image")}
                    size="sm"
                  >
                    Quay lại
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ArticleEditor;
